import React from "react";
import { isEmpty } from "lodash";
import { checkValidURL } from "@/utils/formHelper";
import { QREditorContextProps, QRProps } from "@/contexts/QREditorContext/types";
import { QREditorContext } from "@/contexts/QREditorContext";
import { TimezonesService } from "@/services/TimezonesService";
import { TCalendarEvent } from "@/services/AddToCalendarService/types.ts";
import { PreviewLayout } from ".";
import { Icons } from "@/types/icons";
import { Typography } from "../Typography";
import { IconList } from "../IconList";
import { Link } from "../Link";
import { QREventType } from "@/types/qr";
import { LocationType } from "../PreviewLocationBlock/types";
import { getFullAddress } from "@/utils/qrEditorHelper";
import { PreviewHeadingBlock } from "../PreviewHeadingBlock";
import { PreviewEventDateBlock } from "../PreviewEventDateBlock";
import { PreviewLocationBlock } from "../PreviewLocationBlock";
import { PreviewPersonalInformationBlock } from "../PreviewPersonalInformationBlock";
import { LabeledDataType } from "../LabeledData/types";
import { PAGES } from "@/internals/constants/pages";
import { useTranslation } from "react-i18next";

export default function PreviewEvent(props: QRProps<"event">) {
  const { t } = useTranslation(PAGES.QR_EVENT);

  const qrEditorContextData = React.useContext<QREditorContextProps<"event">>(QREditorContext);
  const isInitPreview = props?.initPreview;

  const data = props?.data || qrEditorContextData?.qrData.data;

  const {
    eventDateTime,
    logoUrl,
    infoTitle,
    infoDescription,
    infoActionTitle,
    infoActionUrl,
    themeColor,
    facilities,
  } = data;

  const getTranslatedText = (staticText: string, translationKey: string) =>
    isInitPreview || !t ? staticText : t(translationKey);

  const getPreviewFormattedData = (data: QREventType) => {
    const {
      locationAddress,
      locationNumeration,
      locationPostalCode,
      locationCity,
      locationState,
      locationCountry,
      organizerName,
      organizerPhone,
      organizerEmail,
      organizerWebsite,
      organizerAbout,
    } = data;

    const addressData: LocationType = {
      address: locationAddress,
      numeration: locationNumeration,
      zipCode: locationPostalCode,
      city: locationCity,
      state: locationState,
      country: locationCountry,
      fullAddress: getFullAddress(
        locationAddress,
        locationNumeration,
        locationPostalCode,
        locationCity,
        locationState,
        locationCountry
      ),
    };

    const organizerData: LabeledDataType[] = [
      {
        label: getTranslatedText("Name", "preview_organizer_name"),
        value: organizerName,
      },
      {
        label: getTranslatedText("Phone", "preview_organizer_phone"),
        value: organizerPhone,
      },
      {
        label: getTranslatedText("Email", "preview_organizer_email"),
        value: organizerEmail && (
          <Link
            externalLink
            className="business-link"
            href={`mailto:${organizerEmail}`}
            prefetch={false}
          >
            {organizerEmail}
          </Link>
        ),
      },
      {
        label: getTranslatedText("Website", "preview_organizer_website"),
        value: organizerWebsite && (
          <Link
            externalLink
            className="business-link"
            href={checkValidURL(organizerWebsite) || ""}
            prefetch={false}
          >
            {organizerWebsite}
          </Link>
        ),
      },
      {
        label: getTranslatedText("About", "preview_organizer_about"),
        value: organizerAbout,
      },
    ];

    return {
      addressData,
      organizerData,
    };
  };

  const { addressData, organizerData } = getPreviewFormattedData(data);

  const calendarEvent: TCalendarEvent = {
    name: infoTitle || getTranslatedText("Event Name", "preview_event_name"),
    details: infoDescription || "",
    location: addressData?.fullAddress,
    startsAt: TimezonesService.convertDateFromTimezoneToLocalTimezone(
      eventDateTime?.start?.date,
      eventDateTime?.allDay ? "0:00" : eventDateTime?.start?.time,
      eventDateTime?.timezone
    ),
    endsAt: TimezonesService.convertDateFromTimezoneToLocalTimezone(
      eventDateTime?.end?.date,
      eventDateTime?.allDay ? "23:59" : eventDateTime?.end?.time,
      eventDateTime?.timezone
    ),
    timezone: eventDateTime?.timezone,
    allDay: eventDateTime?.allDay,
  };

  return (
    <PreviewLayout
      page="event"
      dataQA="preview-event-container"
      backgroundColor={themeColor?.backgroundColor}
      includeBrowser
      isInitPreview={isInitPreview}
    >
      <PreviewHeadingBlock
        isInitPreview={isInitPreview}
        image={logoUrl}
        title={infoTitle}
        text={infoDescription}
        actionText={infoActionTitle}
        actionUrl={infoActionUrl}
        actionColor={themeColor?.buttonColor}
      />

      <div className="qr-content">
        <PreviewEventDateBlock
          title={getTranslatedText("Date of the event", "preview_time")}
          data={eventDateTime}
          labels={{
            start: getTranslatedText("Start", "preview_time_start"),
            end: getTranslatedText("End", "preview_time_end"),
            schedule: getTranslatedText("Schedule", "preview_time_schedule"),
            allDay: getTranslatedText("All Day", "preview_all_day"),
          }}
          calendarEvent={calendarEvent}
          themeColor={themeColor}
        />

        <PreviewLocationBlock
          title={getTranslatedText("Location", "preview_location")}
          data={addressData}
        />

        <PreviewPersonalInformationBlock
          title={getTranslatedText("Organizer information", "preview_organizer")}
          iconName={Icons.users}
          data={organizerData}
        />

        {!isEmpty(facilities) && (
          <div className="u-card">
            <Typography
              Tag="p"
              size="m"
              classname="u-semibold"
            >
              {getTranslatedText("Facilities", "preview_facilities")}
            </Typography>
            <IconList
              compact
              items={facilities as unknown as string[]}
            />
          </div>
        )}
      </div>
    </PreviewLayout>
  );
}
